import { RefObject, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../store";
import { pushAnalytics } from "../../analytics";
import { getNextOnfidoPhase, saveOnfidoData } from "../actions";
import { onfidoInitialState, setOnfidoState } from "../reducer";
import { selectOnfidoState } from "../selectors";
import { TOnfidoAnalyticsEvent, TOnfidoParams } from "../types";
import { useOnfido } from "./useOnfido";
import { SdkParameters } from "onfido-sdk-ui/types";

type TUseOnfidoHypeFlow = (props: {
  params: TOnfidoParams | undefined;
  containerRef: RefObject<Element>;
}) => {
  isLoading: boolean;
  hasError: boolean;
};

export const useOnfidoHypeFlow: TUseOnfidoHypeFlow = ({
  params,
  containerRef,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const onfidoState = useSelector(selectOnfidoState);
  const { hasError, isLoading } = onfidoState;

  const analyticsHandler = useCallback(
    (evt: TOnfidoAnalyticsEvent) => {
      const prefix = evt?.detail?.isCrossDevice === true ? "MOB_" : "DESK_";
      const eventName = evt.detail?.eventName;
      dispatch(pushAnalytics({ subStepSlug: `${prefix}${eventName}` }));
    },
    [dispatch],
  );

  const onError = useCallback(() => {
    setOnfidoState({ ...onfidoInitialState, hasError: true });
  }, []);

  const onComplete = useCallback(
    async (onfidoData: Record<string, unknown>) => {
      async function save() {
        await dispatch(saveOnfidoData({ onfidoData }));
      }
      async function next() {
        await dispatch(getNextOnfidoPhase());
      }
      if (onfidoData) {
        await save();
        await next();
      }
    },
    [dispatch],
  ) as SdkParameters["onComplete"];

  // Initialize Onfido SDK
  useOnfido({
    params,
    containerRef,
    analyticsHandler,
    onComplete,
    onError,
  });

  return {
    isLoading,
    hasError,
  };
};
